import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../api/client';
import './Login.css';

// Brand colors lifted from the vitalengine-analytics login so the splash
// gradients and orb match exactly.
const BRAND = {
  blue: '#2563EB',
  green: '#22C55E',
  navy: '#0F172A',
  sky:  '#38BDF8',
  lime: '#D9E021',
};

const ERROR_MESSAGES = {
  domain_not_allowed: 'Your Google account is not part of the VitalEngine workspace.',
  not_invited: 'This Google account has not been invited. Ask an administrator for access.',
  google_failed: 'Google sign-in failed. Please try again.',
  account_disabled: 'This account has been deactivated.',
  no_email: 'We could not read your email from Google.',
};

function MailIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  );
}

function LockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ChevronRightIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

// Animated splash that previews while the auth card slides in. Mirrors the
// vitalengine-analytics intro: a glowing orb, a grid overlay, eight rising
// bars, and a curving line that traces in.
function AnalyticsSplash() {
  const bars = [9, 5, 12, 7, 15, 10, 18, 13];
  return (
    <div className="vd-splash">
      <div className="vd-splash-grid" />

      <div
        className="vd-orb"
        style={{
          background: `radial-gradient(closest-side, ${BRAND.sky}, transparent 70%), radial-gradient(closest-side, ${BRAND.green}, transparent 70%)`,
        }}
      />

      <div className="vd-splash-bars">
        <div className="vd-bars-row">
          {bars.map((h, i) => (
            <div
              key={i}
              className="vd-bar"
              style={{
                background: `linear-gradient(180deg, ${BRAND.sky}, ${BRAND.blue})`,
                '--h': `${h * 8}px`,
                '--delay': `${i * 60}ms`,
              }}
            />
          ))}
        </div>
        <svg className="vd-line-svg" viewBox="0 0 560 220" fill="none">
          <defs>
            <linearGradient id="vdLineG" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%"  stopColor={BRAND.green} />
              <stop offset="100%" stopColor={BRAND.sky} />
            </linearGradient>
          </defs>
          <path
            className="vd-line"
            d="M 20 170 C 120 150 160 140 220 120 S 360 80 540 40"
            stroke="url(#vdLineG)"
            strokeWidth="4"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="vd-splash-tagline">
        <h2>Turning data into decisions</h2>
      </div>
    </div>
  );
}

function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const urlError = params.get('error');

  const [showSplash, setShowSplash] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(urlError ? (ERROR_MESSAGES[urlError] || 'Sign-in failed.') : '');
  const [submitting, setSubmitting] = useState(false);

  // Match the external project's 2.2s splash, then slide the card in.
  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 2200);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data?.authenticated) {
        const redirectTo = params.get('returnUrl') || '/';
        navigate(redirectTo, { replace: true });
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const googleStartUrl = `${API_BASE_URL}/api/auth/google/start`;

  return (
    <div
      className="vd-login-page"
      style={{
        background:
          `radial-gradient(1400px 800px at 10% -20%, rgba(56,189,248,0.12), transparent 60%),
           radial-gradient(1200px 800px at 120% 120%, rgba(34,197,94,0.12), transparent 60%),
           linear-gradient(120deg, ${BRAND.blue}, ${BRAND.navy})`,
      }}
    >
      <div
        className="vd-beam"
        style={{
          background: `conic-gradient(from 180deg, ${BRAND.blue}, ${BRAND.green}, ${BRAND.lime}, ${BRAND.blue})`,
        }}
      />

      <div className="vd-login-stage">
        <div className={`vd-splash-overlay ${showSplash ? 'is-on' : 'is-off'}`}>
          <AnalyticsSplash />
        </div>

        <div className={`vd-login-card-wrap ${showSplash ? 'is-hidden' : 'is-visible'}`}>
          <div className="vd-login-card">
            <div className="vd-login-brand">
              <div className="vd-login-logo">
                <img
                  src="/vitalengine-logo.png"
                  alt="VitalData"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <span className="vd-login-logo-fallback">VD</span>
              </div>
              <h1>VitalData</h1>
              <p>Customer Success Tool for VitalEngine</p>
            </div>

            <form onSubmit={handleSubmit} className="vd-login-form">
              <label className="vd-field">
                <span className="vd-field-label">Email Address</span>
                <span className="vd-field-input">
                  <MailIcon className="vd-field-icon" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    autoComplete="username"
                    disabled={submitting}
                    required
                  />
                </span>
              </label>

              <label className="vd-field">
                <span className="vd-field-label">Password</span>
                <span className="vd-field-input">
                  <LockIcon className="vd-field-icon" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={submitting}
                    required
                  />
                </span>
              </label>

              {error && <div className="vd-login-error">{error}</div>}

              <button type="submit" className="vd-btn vd-btn-primary" disabled={submitting}>
                {submitting ? <span>Signing In...</span> : (
                  <>
                    Sign In
                    <ChevronRightIcon className="vd-btn-icon" />
                  </>
                )}
              </button>

              <div className="vd-divider">
                <span>Or continue with</span>
              </div>

              <a className="vd-btn vd-btn-ghost vd-btn-google" href={googleStartUrl}>
                <svg className="vd-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 11v3.6h5.09c-.22 1.3-1.54 3.8-5.09 3.8-3.06 0-5.56-2.52-5.56-5.6S8.94 7.2 12 7.2c1.74 0 2.9.74 3.57 1.37l2.43-2.35C16.69 5.02 14.53 4 12 4 6.9 4 2.8 8.03 2.8 13.2 2.8 18.36 6.9 22.4 12 22.4c6.9 0 9.2-4.84 9.2-7.35 0-.5-.05-.83-.11-1.19H12z" fill="currentColor" />
                </svg>
                Sign in with Google
              </a>

              <div className="vd-login-links">
                <Link to="/forgot-password">Forgot password?</Link>
              </div>

              <p className="vd-login-footer">
                © {new Date().getFullYear()} VitalEngine, Inc
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
